import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { LessonStatus } from '../../models';

/**
 * Lesson status icon component
 * Shows different icons based on lesson completion status:
 * - Completed: Green checkmark
 * - In Progress / Available / Failed: Lesson number rendered as SVG text
 * - Locked: Lock icon
 *
 * Numbers are rendered inside an SVG <text> element so that centering
 * is controlled by the SVG coordinate system rather than the CSS box
 * model, eliminating cross-browser/platform inconsistencies.
 */
@Component({
  selector: 'ut-lesson-status-icon',
  templateUrl: './lesson-status-icon.component.html',
  styleUrls: ['./lesson-status-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LessonStatusIconComponent {
  @Input() status: LessonStatus = 'available';
  @Input() lessonNumber: number = 1;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';

  get iconClass(): string {
    return `status-${this.status} size-${this.size}`;
  }

  get showCheckmark(): boolean {
    return this.status === 'completed';
  }

  get showNumber(): boolean {
    return this.status === 'in-progress' || this.status === 'available' || this.status === 'failed';
  }

  get showLock(): boolean {
    return this.status === 'locked';
  }

  get ariaLabel(): string {
    if (this.status === 'completed') {
      return `Lesson ${this.lessonNumber} completed`;
    }
    if (this.status === 'locked') {
      return `Lesson ${this.lessonNumber} locked`;
    }
    if (this.status === 'in-progress') {
      return `Lesson ${this.lessonNumber} in progress`;
    }
    if (this.status === 'failed') {
      return `Lesson ${this.lessonNumber} failed`;
    }
    return `Lesson ${this.lessonNumber}`;
  }
}
